import { ActivationLinkInterface } from '@account/interfaces';
import { AccountMenuService } from '@account/services';
import { AccountEmailsTable, EmailsTableEvent } from '@account/services/tables/emails-table/account-emails.table';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { ShowView } from '@app/modules/common/views';
import { LanguageForm, PasswordForm } from '@app/modules/forms/account/components';
import { EmailForm } from '@app/modules/forms/employees/components';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AnalyticService, SettingsService, ZenDeskService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { TranslationStorageService } from '@app/services/translation-storage.service';
import { BaseForm } from '@forms/base.form';
import { Account } from '@models/account/account.model';
import { Email } from '@models/account/email.model';
import { Employee } from '@models/employee/employee.model';
import { Subscription } from 'rxjs';

type AccountShowDialogType = 'LANGUAGE' | 'PASSWORD' | 'ADD_EMAIL';

@Component({
    templateUrl: './account-show.template.html',
    styleUrls: ['../../../../../../styles/shared-views/profile-account.scss'],
})
export class AccountShowView extends ShowView implements OnDestroy {
    @ViewChild('confirmDeleteEmail', { static: true }) confirmDeleteEmail!: ConfirmationDialogComponent;
    @ViewChild('confirmMakePrimary', { static: true }) confirmMakePrimary!: ConfirmationDialogComponent;

    isSelfEmployeeView = true;

    isLoading = false;

    employee: Employee = this.route.snapshot.data.models.get('employee');
    employeeIsTerminated: boolean = this.employee.isTerminated();
    account: Account = this.employee.account;
    emailsTable?: AccountEmailsTable;
    canLoginAs: boolean =
        this.auth.can(Account.loginAsPermission) &&
        this.employee.canBeLoggedInAs() &&
        this.account.isActive &&
        !this.account.isMultiCompany;
    isOwnAccount: boolean = this.account.id === this.auth.accountId;
    showAccountInformation: boolean =
        this.hasAccountAdminPermissions && !this.isOwnAccount && !this.employeeIsTerminated;
    activationLink: ActivationLinkInterface | null = null;
    settingsSubscription: Subscription | undefined;

    currentlyOpenDialog?: AccountShowDialogType = undefined;

    constructor(
        public route: ActivatedRoute,
        public tabMenuService: AccountMenuService,
        protected auth: AuthService,
        protected element: ElementRef,
        protected notify: NotifyService,
        protected settingsService: SettingsService,
        protected analyticsService: AnalyticService,
        public translationStorage: TranslationStorageService,
        public featureService: FeatureService,
        protected zenDeskService?: ZenDeskService
    ) {
        super();
        this.shouldGetActivationLink() && this.getActivationLink();
        if (this.account?.id) {
            this.emailsTable = new AccountEmailsTable(this.auth, this.settingsService, this.account);
        }
    }

    onSaveLanguage(languageForm: LanguageForm): void {
        const { employee } = languageForm;
        // Pendo Tracking
        if (this.isSelfEmployeeView) {
            this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabEditInfo);
        }
        employee
            .param('company', employee.companyId)
            .save()
            .then(async () => {
                if (!this.employee.language) {
                    throw new Error('account.saveError');
                }
                this.closeDialog();
                this.notify.success('saved');
                this.translationStorage.locale = this.employee.language;
                this.zenDeskService?.clearSession();
                // We need to reload the page so Transifex gets fresh content from ngx-translate
                window.location.reload();
            })
            .catch((err) => {
                this.notify.error(err.message);
                languageForm.addExternalError();
            });
    }

    get hasAccountAdminPermissions(): boolean {
        const canEditAccount =
            this.auth.can(Account.permission.some('update')) && this.auth.canAccountEdit(this.account, 'id');
        return this.canLoginAs || canEditAccount;
    }

    ngOnDestroy(): void {
        this.settingsSubscription?.unsubscribe();
    }

    onCancel(): void {
        this.account.revert();
        this.employee.revert();
        this.closeDialog();
    }

    onSave(form?: BaseForm): void {
        this.isLoading = true;

        this.account
            .save()
            .then(() => {
                this.isLoading = false;
                this.notify.success('account.accountUpdated');
                this.closeDialog();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'account.accountUpdatedError');
                form?.addExternalError();
            });
    }

    onSavePassword(passwordForm: PasswordForm): void {
        passwordForm.validatePasswords();

        if (!passwordForm.hasValidPassword()) {
            // The password form tracks errors outside of the usual Angular Reactive Forms validators unfortunately.
            // This should be refactored at one point to properly validate, but for now we have to add an external error so the form knows to lock/unlock after a failed submission
            passwordForm.addExternalError();
            return;
        }

        passwordForm
            .getPasswordModel()
            .param('account', this.account.id)
            .save()
            .then(() => {
                // Pendo Tracking
                if (this.isSelfEmployeeView) {
                    this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabChangePassword);
                }
                this.notify.success('account.passwordUpdated');
                this.closeDialog();
            })
            .catch((err) => {
                this.notify.error(err.message || 'account.passwordUpdatedError');
                passwordForm.addExternalError();
            });
    }

    onAddEmail(emailForm: EmailForm): void {
        const { email } = emailForm;
        email
            .param('account', this.account.id)
            .save()
            .then(() => {
                // Pendo Tracking
                this.isSelfEmployeeView
                    ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabAddEmail)
                    : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileAccountTabAddNewEmail);

                this.notify.success('account.emailSaved');
                emailForm.reset();
                this.closeDialog();
                this.emailsTable?.reload();
            })
            .catch((err) => {
                this.notify.error(err.message || 'account.emailSavedError');
                emailForm.addExternalError();
            });
    }

    onResendVerification(email: Email): void {
        email
            .resendVerification()
            .then(() => {
                // Pendo Tracking
                if (this.isSelfEmployeeView) {
                    this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabResendEmailVerification);
                }
                this.notify.success('account.emailVerification');
            })
            .catch(() => this.notify.error('account.emailVerificationError'));
    }

    onMakePrimary(email: Email): void {
        this.confirmMakePrimary.open().then((yes: boolean) => {
            if (!yes) {
                return;
            }
            email
                .makePrimary()
                .then(() => {
                    // Pendo Tracking
                    this.isSelfEmployeeView
                        ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabMakeEmailPrimary)
                        : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileAccountTabClickMakePrimary);

                    this.notify.success('account.primaryEmailUpdate');
                    this.emailsTable?.reload();
                })
                .catch(() => this.notify.error('account.primaryEmailUpdateError'));
        });
    }

    async onDeleteEmail(email: Email): Promise<void> {
        const yes = await this.confirmDeleteEmail.open();

        if (!yes) {
            return;
        }

        email
            .param('account', this.account.id)
            .delete()
            .then(() => {
                // Pendo Tracking
                this.isSelfEmployeeView
                    ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileAccountTabDeleteEmail)
                    : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileAccountTabDeleteEmail);

                this.notify.success('account.emailDeleted');
                this.emailsTable?.reload();
            })
            .catch(() => this.notify.error('account.emailDeletedError'));
    }

    menuAction(event: MenuColumnEvent<Email, EmailsTableEvent>): void {
        const { data, menuItem } = event;
        if (!data) {
            return;
        }
        switch (menuItem.event) {
            case 'delete':
                this.onDeleteEmail(data);
                break;
            case 'resend':
                this.onResendVerification(data);
                break;
            case 'primary':
                this.onMakePrimary(data);
        }
    }

    protected shouldGetActivationLink(): boolean {
        return (
            !this.isOwnAccount &&
            this.auth.can(Account.permission.some('view')) &&
            !this.account.isActive &&
            this.employee.status === 'onboarding'
        );
    }

    protected getActivationLink(): void {
        this.account
            .getActivationLink()
            .then((res) => {
                if ('attributes' in res['data']) {
                    const data = res['data']['attributes'];
                    const date = data['expiresAt'];

                    if (!date) {
                        return;
                    }

                    this.activationLink = {
                        link: data.link,
                        expiresAt: {
                            date: date['date'],
                            timezone: date['timezone'],
                            timezone_type: date['timezone_type'],
                        },
                    };
                }
            })
            .catch((err) => this.notify.error(err || 'account.activationLinkError'));
    }
}
